import { Injectable } from '@angular/core';

export interface WeatherEstimate {
    tempF: number;
    city: string;
    condition: string;
    icon: string;              // name in shell/icons.ts
}

// Monthly climate normals (avg high / low, °F, Jan..Dec), rounded from NOAA 1991-2020 data.
interface CityClimate {
    highs: number[];
    lows: number[];
    coastal: boolean;
}

const CLIMATE: { [city: string]: CityClimate } = {
    'Sacramento': { highs: [54, 60, 65, 71, 80, 88, 93, 92, 87, 78, 64, 54], lows: [39, 42, 44, 47, 52, 57, 60, 59, 57, 51, 43, 38], coastal: false },
    'Fresno': { highs: [55, 61, 67, 74, 83, 92, 99, 97, 91, 80, 65, 55], lows: [38, 41, 44, 48, 55, 61, 66, 65, 60, 51, 42, 37], coastal: false },
    'San Jose': { highs: [60, 64, 67, 71, 76, 81, 84, 84, 82, 76, 66, 60], lows: [42, 45, 47, 49, 53, 57, 59, 59, 58, 53, 46, 42], coastal: false },
    'Napa': { highs: [58, 62, 66, 71, 77, 83, 86, 86, 84, 78, 66, 58], lows: [38, 41, 42, 44, 48, 52, 54, 54, 52, 48, 42, 38], coastal: false },
    'San Francisco': { highs: [58, 61, 63, 65, 67, 70, 69, 70, 72, 70, 63, 58], lows: [46, 48, 49, 50, 52, 54, 55, 56, 56, 54, 50, 46], coastal: true },
    'Los Angeles': { highs: [68, 69, 70, 72, 74, 78, 83, 84, 83, 79, 73, 68], lows: [49, 50, 52, 55, 58, 62, 65, 66, 65, 60, 53, 48], coastal: true },
    'San Diego': { highs: [66, 65, 66, 68, 69, 72, 75, 77, 77, 75, 70, 65], lows: [49, 51, 53, 56, 59, 62, 66, 67, 65, 61, 54, 49], coastal: true }
};

// Nearby towns that share a listed city's climate.
const ALIASES: { [city: string]: string } = {
    'roseville': 'Sacramento', 'elk grove': 'Sacramento', 'folsom': 'Sacramento', 'rocklin': 'Sacramento',
    'citrus heights': 'Sacramento', 'rancho cordova': 'Sacramento', 'davis': 'Sacramento', 'orangevale': 'Sacramento',
    'clovis': 'Fresno', 'oakland': 'San Francisco', 'walnut creek': 'San Jose', 'concord': 'San Jose',
    'santa clara': 'San Jose', 'sunnyvale': 'San Jose', 'vallejo': 'Napa', 'fairfield': 'Napa',
    'long beach': 'Los Angeles', 'pasadena': 'Los Angeles', 'irvine': 'Los Angeles', 'chula vista': 'San Diego'
};

const DEFAULT_CITY = 'Sacramento';

/**
 * A believable, static weather reading for California — no weather API and no network calls.
 *
 * Temperature follows the month's normals and the time of day (coolest ~5am, warmest ~3pm).
 * The condition is picked from the season's typical ones using the date as a seed, so it stays
 * the same all day and varies day to day. Swap this service for a real API later; the
 * WeatherEstimate shape is what the header renders.
 */
@Injectable({
    providedIn: 'root'
})
export class WeatherEstimateService {

    estimate(city: string, now: Date = new Date()): WeatherEstimate {
        const name = this.resolveCity(city);
        const climate = CLIMATE[name];
        const month = now.getMonth();
        const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);

        const high = climate.highs[month];
        const low = climate.lows[month];
        const hour = now.getHours() + now.getMinutes() / 60;
        const jitter = (this.seeded(dayOfYear * 7 + 3) - 0.5) * 6;          // +/- 3°F, fixed for the day
        const tempF = Math.round(low + (high - low) * this.dailyCurve(hour) + jitter);

        const options = this.conditions(month, climate.coastal, hour);
        const pick = options[Math.floor(this.seeded(dayOfYear) * options.length)];
        return { tempF, city: name, condition: pick[0], icon: pick[1] };
    }

    private resolveCity(city: string): string {
        const key = (city || '').trim().toLowerCase();
        const direct = Object.keys(CLIMATE).find(c => c.toLowerCase() === key);
        return direct || ALIASES[key] || DEFAULT_CITY;
    }

    // 0 at ~5am, 1 at ~3pm, easing back down overnight.
    private dailyCurve(hour: number): number {
        if (hour >= 5 && hour <= 15) {
            return (1 - Math.cos(Math.PI * (hour - 5) / 10)) / 2;
        }
        const sincePeak = (hour - 15 + 24) % 24;                             // 0..14 hours after 3pm
        return (1 + Math.cos(Math.PI * sincePeak / 14)) / 2;
    }

    // [condition, icon] choices typical for the month.
    private conditions(month: number, coastal: boolean, hour: number): string[][] {
        const clear = hour >= 19 || hour < 6 ? ['Clear', 'moon'] : ['Sunny', 'sun'];
        if (month === 11 || month <= 1) {
            return [['Cloudy', 'cloud'], ['Rain likely', 'rain'], ['Partly cloudy', 'cloud-sun'], coastal ? ['Showers', 'rain'] : ['Foggy', 'fog']];
        }
        if (month <= 3) {
            return [['Partly cloudy', 'cloud-sun'], clear, ['Showers', 'rain'], ['Partly cloudy', 'cloud-sun']];
        }
        if (month <= 7) {
            return coastal && hour < 11
                ? [['Morning fog', 'fog'], ['Morning fog', 'fog'], ['Partly cloudy', 'cloud-sun']]
                : [clear, clear, ['Partly cloudy', 'cloud-sun']];
        }
        return [clear, clear, ['Partly cloudy', 'cloud-sun']];
    }

    // Deterministic 0..1 from an integer seed.
    private seeded(seed: number): number {
        const x = Math.sin(seed * 12.9898) * 43758.5453;
        return x - Math.floor(x);
    }
}
